/**
 * Router Navigation Guards
 * Authentication and role-based access control
 */

import type { RouteLocationNormalized, Router } from 'vue-router'
import { useAuthStore } from '@/stores'
import { syncCookiePreferencesButtonVisibility } from '@/utils/cookiePreferences'
import { t } from '@/i18n'

// Extend route meta type
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiredRole?: 'owner' | 'pharmacist'
    title?: string
  }
}

/**
 * Setup all navigation guards on the router
 */
export function setupGuards(router: Router): void {
  router.beforeEach(async (to, _from) => {
    const authStore = useAuthStore()

    if (!authStore.isInitialized) {
      try {
        await authStore.initialize()
      } catch (error) {
        console.error('Auth initialization failed:', error)
        await authStore.logout()
        if (!isAuthRoute(to)) {
          return {
            name: 'Login',
            query: {
              redirect: to.fullPath,
              error: 'session_expired',
            },
          }
        }
      }
    }

    const explicitlyPublic = to.meta.requiresAuth === false
    const requiresAuth = explicitlyPublic
      ? false
      : to.matched.some(record => record.meta.requiresAuth === true)

    if (requiresAuth && !authStore.isAuthenticated) {
      return {
        name: 'Login',
        query: { redirect: to.fullPath },
      }
    }

    if (authStore.isAuthenticated && isAuthRoute(to)) {
      return { name: authStore.isOwner ? 'OwnerDashboard' : 'Dashboard' }
    }

    const requiredRole = to.matched.find(record => record.meta.requiredRole)?.meta.requiredRole
    if (requiredRole && authStore.user?.role !== requiredRole) {
      if (authStore.isOwner) {
        return { name: 'OwnerDashboard' }
      }
      return { name: 'Dashboard' }
    }

    return true
  })

  router.afterEach((to) => {
    // `meta.title` holds an i18n key (e.g. 'routes.login'); translate it to the
    // active language so the browser/document title matches the UI locale.
    const key = (to.meta.title as string | undefined) || getDefaultTitleKey(to)
    const title = key ? t(key) : ''
    document.title = title ? `${title} | Retos P&G Health` : 'Retos P&G Health'
    syncCookiePreferencesButtonVisibility(to.name)
  })
}

function isAuthRoute(to: RouteLocationNormalized): boolean {
  const authRoutes = ['Login', 'Register', 'ForgotPassword', 'RegisterPending']
  return authRoutes.includes(to.name as string)
}

// Fallback i18n title keys by route name, used when a route omits meta.title.
function getDefaultTitleKey(to: RouteLocationNormalized): string {
  const keys: Record<string, string> = {
    Login: 'routes.login',
    Register: 'routes.register',
    RegisterPending: 'routes.registerPending',
    ForgotPassword: 'routes.forgotPassword',
    Dashboard: 'routes.dashboard',
    Challenges: 'routes.challenges',
    TicketStatus: 'routes.ticketStatus',
    CashBalance: 'routes.cashBalance',
    Profile: 'routes.profile',
    Notifications: 'routes.notifications',
    EmployeeApprovals: 'routes.employeeApprovals',
    Privacy: 'routes.privacy',
    OwnerDashboard: 'routes.ownerDashboard',
    NotFound: 'routes.notFound',
  }
  return keys[to.name as string] || ''
}

export async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized
) {
  const authStore = useAuthStore()

  if (!authStore.isInitialized) {
    await authStore.initialize()
  }

  if (!authStore.isAuthenticated) {
    return {
      name: 'Login',
      query: { redirect: to.fullPath },
    }
  }

  return true
}

export async function ownerGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized
) {
  const authStore = useAuthStore()

  if (!authStore.isInitialized) {
    await authStore.initialize()
  }

  if (!authStore.isAuthenticated) {
    return {
      name: 'Login',
      query: { redirect: to.fullPath },
    }
  }

  if (!authStore.isOwner) {
    return { name: 'Dashboard' }
  }

  return true
}

export async function guestGuard() {
  const authStore = useAuthStore()

  if (!authStore.isInitialized) {
    await authStore.initialize()
  }

  if (authStore.isAuthenticated) {
    return { name: 'Dashboard' }
  }

  return true
}
